import {
  makeStyles,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions
} from "@material-ui/core";
import {
  KitProfileCardFragment,
  ApproveBookingKit,
  RejectBookingKit,
  RejectDoneBookingKit,
  TimeReportKit_GetKit,
  TimereportKitSupportSearch_Get,
  useDeleteBookingKitMutation,
  useGetKitApprovalConfigQuery,
  GetKitApprovalConfig,
  GetKit
} from "@graphql";
import React, { useState } from "react";
import locale from "@bonliva-crm/locale";
import { useConfirmDialog, useDialog, useGlobalStore, useSnackbars } from "@hooks";
import { BookingKitChecks } from "./booking-kit-checks";
import { OkCancelDialogOptions } from "@containers";
import { useLegacyNavigation } from "@navigation";

type Props = {
  kit: KitProfileCardFragment;
};

export const BookingKitActions = ({ kit }: Props) => {
  const classes = useStyles();
  const snackbars = useSnackbars();
  const rejectDialog = useDialog();
  const [rejectComment, setRejectComment] = useState("");
  const [mutating, setMutating] = useState(false);

  const { navigation } = useGlobalStore();
  const legacyNavigation = useLegacyNavigation();

  const confirm = useConfirmDialog();
  const { user } = useGlobalStore();

  const { data: approvalConfig, loading: loadingApprovalConfig } =
    useGetKitApprovalConfigQuery({
      variables: { id: kit._id },
      // The kit can be approved by someone else (or advanced by the
      // workflow) between visits — never trust a cached config alone.
      fetchPolicy: "cache-and-network"
    });

  const toggleRejectDialog = () => {
    rejectDialog.open ? rejectDialog.onClose() : rejectDialog.onOpen();
  };

  const handleApprove = (kitId: string) => {
    setMutating(true);
    return ApproveBookingKit.func(
      {
        id: kitId
      },
      {
        refetchQueries: [
          TimeReportKit_GetKit.OperationName,
          TimereportKitSupportSearch_Get.OperationName,
          GetKitApprovalConfig.OperationName
        ]
      }
    )
      .then(() => {
        snackbars.showSuccess(`Kit successfully updated`);
        navigation.close(legacyNavigation.name);
      })
      .catch((err) => {
        snackbars.showError(`Cannot approve tr kit\n ${err}`);
      })
      .finally(() => {
        setMutating(false);
      });
  };

  const handleReject = (comment: string, kitId: string, isDone: boolean) => {
    const mutation = isDone ? RejectDoneBookingKit : RejectBookingKit;
    setMutating(true);
    mutation
      .func(
        {
          id: kitId,
          comment: comment.trim()
        },
        {
          refetchQueries: [
            TimeReportKit_GetKit.OperationName,
            TimereportKitSupportSearch_Get.OperationName,
            GetKitApprovalConfig.OperationName
          ]
        }
      )
      .then(() => {
        snackbars.showSuccess(`Kit successfully updated`);
        toggleRejectDialog();
        navigation.close(legacyNavigation.name);
      })
      .catch((err) => {
        snackbars.showError(`Cannot reject tr kit\n ${err}`);
      })
      .finally(() => {
        setMutating(false);
      });
  };

  const [deleteBookingKit, { loading: loadingDelete }] = useDeleteBookingKitMutation({
    variables: { id: kit?._id },
    refetchQueries: [GetKit.OperationName, TimeReportKit_GetKit.OperationName]
  });

  const handleDelete = async () => {
    confirm.confirm(new OkCancelDialogOptions()).then((v) => {
      if (v !== "ok") {
        return;
      }

      deleteBookingKit()
        .then(() => {
          snackbars.showSuccess(locale.Deleted);
          navigation.close(legacyNavigation.name);
        })
        .catch((err) => {
          snackbars.showError(`Error deleting: ${err}`);
          console.error(err);
        });
    });
  };

  return (
    kit && (
      <>
        <div className={classes.btnWrap}>
          <BookingKitChecks bookingId={kit?.bookingId} />

          {!kit.deleted && (
            <Button
              disabled={loadingDelete || !user.can("booking-kit:delete:all")}
              onClick={handleDelete}
              variant={"outlined"}
            >
              {locale.Delete}
            </Button>
          )}

          <Button
            color={"primary"}
            variant={"contained"}
            onClick={toggleRejectDialog}
            disabled={
              kit?.deleted ||
              loadingApprovalConfig ||
              mutating ||
              (!approvalConfig?.bookingKitApprovalConfig?.canReject &&
                !approvalConfig?.bookingKitApprovalConfig?.canRejectDone)
            }
          >
            {locale.Reject}
          </Button>

          <Button
            color={"secondary"}
            variant={"contained"}
            disabled={
              kit?.deleted ||
              loadingApprovalConfig ||
              mutating ||
              !approvalConfig?.bookingKitApprovalConfig?.canApprove
            }
            onClick={() => handleApprove(kit?._id)}
          >
            {locale.Approve}
          </Button>
        </div>
        <Dialog
          fullWidth
          maxWidth="md"
          open={rejectDialog.open}
          onClose={toggleRejectDialog}
        >
          <DialogTitle>{locale.Reject}</DialogTitle>
          <DialogContent>
            <TextField
              label={locale.Comment}
              fullWidth
              multiline
              rows={4}
              error={!rejectComment}
              name={"rejectComment"}
              value={rejectComment}
              onChange={(e) => setRejectComment(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={toggleRejectDialog}>{locale.Cancel}</Button>
            <Button
              color={"primary"}
              disabled={!rejectComment || mutating}
              onClick={() => handleReject(rejectComment, kit?._id, kit?.isDone)}
            >
              {locale.Send}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    )
  );
};

const useStyles = makeStyles((theme) => ({
  btnWrap: {
    display: "flex",
    gap: theme.spacing(1)
  },
  deletedChip: {
    borderColor: "#000",
    color: "#fff",
    height: "auto"
  }
}));
